/**
 * Macro Progress Insights
 * Data-driven progress tracking and recommendations
 */

import { useState } from 'react';
import { TrendingDown, TrendingUp, Minus, AlertTriangle, CheckCircle2, Target, Scale, Info, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  useClientActiveGoal,
  useProgressAnalysis,
  useClientWeightLogs,
  useAddWeightLog,
} from '@/hooks/use-macro-calculator';
import { GOAL_LABELS, formatWeeklyChange, formatCalorieAdjustment } from '@/lib/nutrition/macro-calculator';
import type { ProgressAnalysis } from '@/lib/nutrition/macro-insights';
import type { NutritionGoalWithPlan } from '@/lib/nutrition/types';

// =============================================================================
// Types
// =============================================================================

interface MacroProgressInsightsProps {
  clientId: string;
  onSetupClick?: () => void;
  onAdjustClick?: () => void;
  compact?: boolean;
}

// =============================================================================
// Status Components
// =============================================================================

function StatusBadge({ status }: { status: ProgressAnalysis['status'] }) {
  const config = {
    on_track: { label: 'On Track', variant: 'default' as const, icon: CheckCircle2 },
    ahead: { label: 'Ahead of Target', variant: 'default' as const, icon: TrendingDown },
    behind: { label: 'Behind Target', variant: 'secondary' as const, icon: AlertTriangle },
    plateau: { label: 'Plateau Detected', variant: 'destructive' as const, icon: Minus },
    insufficient_data: { label: 'Need More Data', variant: 'outline' as const, icon: Info },
  };

  const { label, variant, icon: Icon } = config[status];

  return (
    <Badge variant={variant} className="gap-1">
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

function WeightTrendIcon({ trend }: { trend: 'up' | 'down' | 'stable' }) {
  if (trend === 'down') return <TrendingDown className="h-4 w-4 text-success" />;
  if (trend === 'up') return <TrendingUp className="h-4 w-4 text-warning" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

// =============================================================================
// Main Component
// =============================================================================

export function MacroProgressInsights({
  clientId,
  onSetupClick,
  onAdjustClick,
  compact = false,
}: MacroProgressInsightsProps) {
  const { data: activeGoal, isLoading: goalLoading } = useClientActiveGoal(clientId);
  const { data: progress, isLoading: progressLoading } = useProgressAnalysis(clientId);
  const { data: weightLogs } = useClientWeightLogs(clientId);

  const isLoading = goalLoading || progressLoading;

  // No active plan
  if (!isLoading && !activeGoal) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-6">
            <Target className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="font-medium mb-2">No Nutrition Plan Set</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create a data-driven macro plan to track progress and get insights.
            </p>
            {onSetupClick && (
              <Button onClick={onSetupClick}>
                Set Up Macro Plan
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Has plan but no progress data yet
  if (!isLoading && activeGoal && !progress) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Macro Progress</span>
            {activeGoal.goal_type && (
              <Badge variant="outline">{GOAL_LABELS[activeGoal.goal_type]}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <Scale className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">
              Waiting for nutrition and weight data to generate insights.
            </p>
          </div>
          <CurrentTargetsDisplay goal={activeGoal} />
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/3" />
            <div className="h-8 bg-muted rounded w-2/3" />
            <div className="h-4 bg-muted rounded w-1/2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full progress view
  if (activeGoal && progress) {
    if (compact) {
      return <CompactProgressView goal={activeGoal} progress={progress} onSetupClick={onSetupClick} />;
    }

    return <FullProgressView goal={activeGoal} progress={progress} weightLogs={weightLogs || []} onAdjustClick={onAdjustClick} />;
  }

  return null;
}

// =============================================================================
// Sub-Components
// =============================================================================

function CurrentTargetsDisplay({ goal }: { goal: NutritionGoalWithPlan }) {
  return (
    <div className="grid grid-cols-4 gap-2 mt-4 pt-4 border-t">
      <div className="text-center">
        <div className="text-lg font-bold">{goal.target_calories}</div>
        <div className="text-xs text-muted-foreground">Calories</div>
      </div>
      <div className="text-center">
        <div className="text-lg font-bold text-chart-2">{goal.target_protein_g}g</div>
        <div className="text-xs text-muted-foreground">Protein</div>
      </div>
      <div className="text-center">
        <div className="text-lg font-bold text-chart-3">{goal.target_carbs_g}g</div>
        <div className="text-xs text-muted-foreground">Carbs</div>
      </div>
      <div className="text-center">
        <div className="text-lg font-bold text-chart-1">{goal.target_fat_g}g</div>
        <div className="text-xs text-muted-foreground">Fat</div>
      </div>
    </div>
  );
}

function CompactProgressView({
  goal,
  progress,
  onSetupClick,
}: {
  goal: NutritionGoalWithPlan;
  progress: ProgressAnalysis;
  onSetupClick?: () => void;
}) {
  return (
    <Card interactive onClick={onSetupClick}>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              'p-2 rounded-full',
              progress.status === 'on_track' || progress.status === 'ahead'
                ? 'bg-success/15 text-success'
                : progress.status === 'behind'
                  ? 'bg-warning/15 text-warning'
                  : 'bg-destructive/15 text-destructive'
            )}>
              <Target className="h-5 w-5" />
            </div>
            <div>
              <div className="font-medium">
                {goal.goal_type ? GOAL_LABELS[goal.goal_type] : 'Nutrition'} Progress
              </div>
              <div className="text-sm text-muted-foreground">
                {progress.days_on_plan} days • {progress.calorie_adherence_percent}% adherence
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={progress.status} />
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FullProgressView({
  goal,
  progress,
  weightLogs,
  onAdjustClick,
}: {
  goal: NutritionGoalWithPlan;
  progress: ProgressAnalysis;
  weightLogs: Array<{ weight_lbs: number; logged_date: string }>;
  onAdjustClick?: () => void;
}) {
  const latestWeight = weightLogs[0]?.weight_lbs;
  const startWeight = goal.starting_weight_lbs;
  const totalChange = latestWeight && startWeight ? latestWeight - startWeight : 0;
  const weightTrend = totalChange < -0.5 ? 'down' : totalChange > 0.5 ? 'up' : 'stable';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Progress Insights</CardTitle>
            <CardDescription>
              {progress.days_on_plan} days on {goal.goal_type ? GOAL_LABELS[goal.goal_type].toLowerCase() : 'plan'}
            </CardDescription>
          </div>
          <StatusBadge status={progress.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-3 gap-4">
          {/* Weight Change */}
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-center gap-1 mb-1">
              <WeightTrendIcon trend={weightTrend} />
              <span className="text-xl font-bold">
                {totalChange > 0 ? '+' : ''}{totalChange.toFixed(1)}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">lbs total</div>
            {startWeight && latestWeight && (
              <div className="text-xs text-muted-foreground mt-1">
                {startWeight} → {latestWeight} lbs
              </div>
            )}
          </div>

          {/* Adherence */}
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="text-xl font-bold">{progress.calorie_adherence_percent}%</div>
            <div className="text-xs text-muted-foreground">Adherence</div>
            <Progress
              value={progress.calorie_adherence_percent}
              className="h-1.5 mt-2"
            />
          </div>

          {/* Average Intake */}
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="text-xl font-bold">{progress.average_intake}</div>
            <div className="text-xs text-muted-foreground">Avg cal/day</div>
            <div className={cn(
              'text-xs mt-1',
              Math.abs(progress.average_intake - progress.target_calories) <= 100
                ? 'text-success'
                : 'text-warning'
            )}>
              Target: {progress.target_calories}
            </div>
          </div>
        </div>

        {/* Progress vs Expected */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Expected change:</span>
            <span>{progress.expected_change_lbs > 0 ? '+' : ''}{progress.expected_change_lbs.toFixed(1)} lbs</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Actual change:</span>
            <span className="font-medium">{progress.actual_change_lbs > 0 ? '+' : ''}{progress.actual_change_lbs.toFixed(1)} lbs</span>
          </div>
          <div className="flex justify-between text-sm border-t pt-2">
            <span className="text-muted-foreground">Variance:</span>
            <span className={cn(
              'font-medium',
              Math.abs(progress.variance_lbs) <= 0.5 ? 'text-success' : 'text-warning'
            )}>
              {progress.variance_lbs > 0 ? '+' : ''}{progress.variance_lbs.toFixed(1)} lbs
            </span>
          </div>
        </div>

        {/* Insight */}
        <Alert variant={
          progress.status === 'on_track' || progress.status === 'ahead'
            ? 'default'
            : progress.status === 'plateau'
              ? 'destructive'
              : 'default'
        }>
          <Info className="h-4 w-4" />
          <AlertTitle>Insight</AlertTitle>
          <AlertDescription>{progress.insight}</AlertDescription>
        </Alert>

        {/* Recommendation */}
        {progress.recommendation && (
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-full">
                  <Target className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">Recommendation</div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {progress.recommendation.reason}
                  </p>
                  {progress.recommendation.adjustment_calories && (
                    <div className="mt-2 text-sm">
                      Suggested adjustment:{' '}
                      <span className="font-medium">
                        {formatCalorieAdjustment(progress.recommendation.adjustment_calories)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {onAdjustClick && progress.recommendation.type !== 'review_adherence' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 w-full"
                  onClick={onAdjustClick}
                >
                  Adjust Plan
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Current Targets */}
        <CurrentTargetsDisplay goal={goal} />
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Weight Log Quick Entry
// =============================================================================

export function WeightLogQuickEntry({
  clientId,
  onSuccess,
}: {
  clientId: string;
  onSuccess?: () => void;
}) {
  const { data: weightLogs } = useClientWeightLogs(clientId);
  const addWeight = useAddWeightLog();
  const [weight, setWeight] = useState('');

  const latestWeight = weightLogs?.[0]?.weight_lbs;
  const latestDate = weightLogs?.[0]?.logged_date;
  const isToday = latestDate === new Date().toISOString().split('T')[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!weight) return;

    await addWeight.mutateAsync({
      client_id: clientId,
      weight_lbs: parseFloat(weight),
    });

    setWeight('');
    onSuccess?.();
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Scale className="h-4 w-4" />
          Log Weight
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isToday ? (
          <div className="text-center py-2">
            <div className="text-2xl font-bold">{latestWeight} lbs</div>
            <div className="text-xs text-muted-foreground">Logged today</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              type="number"
              step="0.1"
              placeholder={latestWeight ? String(latestWeight) : 'Weight'}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="flex-1"
            />
            <Button type="submit" size="sm" disabled={addWeight.isPending || !weight}>
              {addWeight.isPending ? '...' : 'Log'}
            </Button>
          </form>
        )}
        {latestWeight && !isToday && (
          <div className="text-xs text-muted-foreground mt-2 text-center">
            Last: {latestWeight} lbs on {new Date(latestDate!).toLocaleDateString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

